/** `${factorId}:${questionIndex}` → answerId */
export type MatrixSelections = Record<string, number>;

export type PositionGrading = {
  position: string;
  familyId: number;
  familyName: string;
  roleClusterId: number;
  roleClusterName: string;
  selections: MatrixSelections;
  roleId: number;
  roleName: string;
  gradeLevel: number;
  updatedAt: string;
};

export type EmployeeGradingOverride = {
  employeeId: string;
  familyId: number;
  familyName: string;
  roleClusterId: number;
  roleClusterName: string;
  selections: MatrixSelections;
  roleId: number;
  roleName: string;
  gradeLevel: number;
  updatedAt: string;
};

const DB_NAME = 'datacircle';
const DB_VERSION = 2;
const EMPLOYEES_STORE = 'employees';
const POSITION_GRADINGS_STORE = 'position_gradings';
const EMPLOYEE_GRADINGS_STORE = 'employee_gradings';

function normalizePositionKey(position: string): string {
  return position.trim().toLowerCase();
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(EMPLOYEES_STORE)) {
        db.createObjectStore(EMPLOYEES_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(POSITION_GRADINGS_STORE)) {
        db.createObjectStore(POSITION_GRADINGS_STORE, { keyPath: 'positionKey' });
      }
      if (!db.objectStoreNames.contains(EMPLOYEE_GRADINGS_STORE)) {
        db.createObjectStore(EMPLOYEE_GRADINGS_STORE, { keyPath: 'employeeId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Failed to open IndexedDB'));
  });
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

async function runTx<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb();
  try {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const result = await fn(store);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    });
    return result;
  } finally {
    db.close();
  }
}

type PositionGradingRecord = PositionGrading & { positionKey: string };

export async function listPositionGradings(): Promise<PositionGrading[]> {
  return runTx(POSITION_GRADINGS_STORE, 'readonly', async (store) => {
    const items = (await reqToPromise(store.getAll())) as PositionGradingRecord[];
    return items
      .map(({ positionKey: _pk, ...rest }) => rest)
      .sort((a, b) => a.position.localeCompare(b.position));
  });
}

export async function getPositionGrading(position: string): Promise<PositionGrading | null> {
  const positionKey = normalizePositionKey(position);
  if (!positionKey) return null;
  return runTx(POSITION_GRADINGS_STORE, 'readonly', async (store) => {
    const row = (await reqToPromise(store.get(positionKey))) as PositionGradingRecord | undefined;
    if (!row) return null;
    const { positionKey: _pk, ...rest } = row;
    return rest;
  });
}

export async function savePositionGrading(grading: PositionGrading): Promise<void> {
  const positionKey = normalizePositionKey(grading.position);
  if (!positionKey) throw new Error('Укажите название должности');
  const record: PositionGradingRecord = { ...grading, positionKey };
  await runTx(POSITION_GRADINGS_STORE, 'readwrite', async (store) => {
    await reqToPromise(store.put(record));
  });
}

export async function deletePositionGrading(position: string): Promise<void> {
  const positionKey = normalizePositionKey(position);
  if (!positionKey) return;
  await runTx(POSITION_GRADINGS_STORE, 'readwrite', async (store) => {
    await reqToPromise(store.delete(positionKey));
  });
}

export async function listEmployeeGradingOverrides(): Promise<EmployeeGradingOverride[]> {
  return runTx(EMPLOYEE_GRADINGS_STORE, 'readonly', async (store) => {
    return (await reqToPromise(store.getAll())) as EmployeeGradingOverride[];
  });
}

export async function getEmployeeGradingOverride(employeeId: string): Promise<EmployeeGradingOverride | null> {
  return runTx(EMPLOYEE_GRADINGS_STORE, 'readonly', async (store) => {
    return (await reqToPromise(store.get(employeeId))) as EmployeeGradingOverride | null;
  });
}

export async function saveEmployeeGradingOverride(grading: EmployeeGradingOverride): Promise<void> {
  await runTx(EMPLOYEE_GRADINGS_STORE, 'readwrite', async (store) => {
    await reqToPromise(store.put(grading));
  });
}

export async function deleteEmployeeGradingOverride(employeeId: string): Promise<void> {
  await runTx(EMPLOYEE_GRADINGS_STORE, 'readwrite', async (store) => {
    await reqToPromise(store.delete(employeeId));
  });
}
